import type {
  MediaAudio,
  MediaContact,
  MediaDocument,
  MediaImage,
  MediaLocation,
  MediaSticker,
  MediaVideo,
  Message,
} from "@/types/chat";
import {
  DownloadIcon,
  FileIcon,
  MapPinIcon,
  MicIcon,
  UserIcon,
} from "lucide-react";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";

function ImageContent({ media }: { media: MediaImage }) {
  const [open, setOpen] = useState(false);
  const thumbnailSrc = media.thumbnail
    ? `data:image/jpeg;base64,${media.thumbnail}`
    : undefined;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="block rounded overflow-hidden cursor-pointer"
      >
        <img
          src={thumbnailSrc ?? media.mediaUrl}
          alt={media.caption || "Imagem"}
          className="max-w-60 max-h-60 object-cover rounded"
          loading="lazy"
        />
      </button>
      {media.caption && (
        <span className="mt-1 text-sm">{media.caption}</span>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[90vw] max-h-[90vh] p-2 flex items-center justify-center">
          <DialogTitle className="sr-only">Imagem</DialogTitle>
          <img
            src={media.mediaUrl}
            alt={media.caption || "Imagem"}
            className="max-w-full max-h-[85vh] object-contain rounded"
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

function VideoContent({ media }: { media: MediaVideo }) {
  const posterSrc = media.thumbnail
    ? `data:image/jpeg;base64,${media.thumbnail}`
    : undefined;

  return (
    <div className="flex flex-col gap-1">
      <video
        src={media.mediaUrl}
        poster={posterSrc}
        controls
        preload="none"
        className="max-w-60 max-h-60 rounded"
      />
      {media.caption && (
        <span className="text-sm">{media.caption}</span>
      )}
    </div>
  );
}

function AudioContent({ media }: { media: MediaAudio }) {
  if (media.ptt) {
    return (
      <div className="flex items-center gap-2 min-w-40">
        <MicIcon className="size-4 shrink-0 opacity-60" />
        <audio src={media.mediaUrl} controls preload="none" className="h-8 w-full min-w-0" />
      </div>
    );
  }

  return (
    <audio src={media.mediaUrl} controls preload="none" className="max-w-60" />
  );
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function DocumentContent({ media }: { media: MediaDocument }) {
  const sizeLabel = formatFileSize(media.fileSize);

  return (
    <a
      href={`${media.mediaUrl}?download=1&filename=${encodeURIComponent(media.fileName)}`}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 px-3 py-2 rounded bg-background/50 hover:bg-background/80 transition-colors min-w-40"
    >
      <FileIcon className="size-8 shrink-0 opacity-60" />
      <div className="flex flex-col min-w-0">
        <span className="text-sm font-medium truncate">{media.fileName}</span>
        {sizeLabel && (
          <span className="text-xs opacity-60">{sizeLabel}</span>
        )}
      </div>
      <DownloadIcon className="size-4 shrink-0 opacity-60 ml-auto" />
    </a>
  );
}

function StickerContent({ media }: { media: MediaSticker }) {
  return (
    <img
      src={media.mediaUrl}
      alt="Figurinha"
      className="size-32 object-contain"
      loading="lazy"
    />
  );
}

function LocationContent({ media }: { media: MediaLocation }) {
  const mapsUrl = `https://www.google.com/maps?q=${media.latitude},${media.longitude}`;
  const label = media.name || media.address || "Localização";

  return (
    <a
      href={mapsUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 hover:underline"
    >
      <MapPinIcon className="size-4 shrink-0 text-red-500" />
      <div className="flex flex-col min-w-0">
        <span className="text-sm font-medium">{label}</span>
        {media.name && media.address && (
          <span className="text-xs opacity-60">{media.address}</span>
        )}
        <span className="text-xs opacity-60">Abrir no Google Maps</span>
      </div>
    </a>
  );
}

function ContactContent({ media }: { media: MediaContact }) {
  return (
    <div className="flex items-center gap-2">
      <UserIcon className="size-4 shrink-0 opacity-60" />
      <span className="text-sm font-medium">{media.displayName}</span>
    </div>
  );
}

export function MessageContent({ message }: { message: Message }) {
  const { media } = message;

  if (!media) {
    return <span>{message.text}</span>;
  }

  switch (media.type) {
    case "imageMessage":
      return <ImageContent media={media} />;
    case "videoMessage":
      return <VideoContent media={media} />;
    case "audioMessage":
      return <AudioContent media={media} />;
    case "documentMessage":
      return <DocumentContent media={media} />;
    case "stickerMessage":
      return <StickerContent media={media} />;
    case "locationMessage":
      return <LocationContent media={media} />;
    case "contactMessage":
      return <ContactContent media={media} />;
    default:
      return <span>{message.text}</span>;
  }
}

export function isSticker(message: Message): boolean {
  return message.media?.type === "stickerMessage";
}
